<div class="flex flex-col max-h-[80vh]">
    <!-- Header -->
    <div class="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
        <div>
            <h2 class="text-lg font-bold text-gray-900 dark:text-white">Timesheet Details</h2>
            <p class="text-xs text-gray-500 dark:text-gray-400">{{ ts.projectName }}</p>
            <p *ngIf="ts.lastModifiedByName" class="text-xs text-amber-500 mt-0.5">
                Edited by {{ ts.lastModifiedByName }} · {{ formatDateTime(ts.lastModifiedDate) }}
            </p>
            <p *ngIf="!ts.lastModifiedByName && ts.createdByName" class="text-xs text-gray-400 mt-0.5">
                Created by {{ ts.createdByName }} · {{ formatDateTime(ts.createdAt) }}
            </p>
        </div>
        <button mat-icon-button (click)="close()" class="h-8 w-8">
            <mat-icon [svgIcon]="'tabler_outline:x'" class="icon-size-5"></mat-icon>
        </button>
    </div>

    <!-- Scrollable content -->
    <div class="flex-1 overflow-y-auto px-5 py-4 space-y-5">

        <!-- Date & Weather -->
        <div class="grid grid-cols-2 gap-4 text-sm p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50">
            <div>
                <span class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-0.5">Date</span>
                <span class="text-gray-900 dark:text-white">{{ formattedDate }}</span>
            </div>
            <div *ngIf="ts.weatherCondition" class="text-right">
                <span class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-0.5">Weather</span>
                <span class="text-gray-900 dark:text-white">{{ ts.weatherCondition }}</span>
                <span *ngIf="ts.temperature" class="ml-1 text-gray-700 dark:text-gray-300">· {{ ts.temperature }}</span>
            </div>
        </div>

        <!-- Labor -->
        <div *ngIf="activeEmployees.length > 0">
            <h3 class="text-sm font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-1">
                <mat-icon [svgIcon]="'tabler_outline:users'" class="icon-size-4 text-blue-500"></mat-icon>
                Labor
            </h3>
            <div *ngFor="let emp of activeEmployees"
                 class="mb-2 flex items-stretch gap-1.5">
                <div class="flex-1 p-3 rounded-lg border-2 transition-colors"
                     [ngClass]="emp.employee_id === data.highlightEmployeeId
                        ? 'border-blue-400 dark:border-blue-500 bg-blue-50 dark:bg-blue-900/30 ring-1 ring-blue-300 dark:ring-blue-600'
                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900'">
                    <div class="flex items-center justify-between">
                        <span class="font-semibold text-sm text-gray-900 dark:text-white">{{ formatEmployeeName(emp.name) }}</span>
                        <span class="text-xs font-bold text-blue-600 dark:text-blue-400">{{ totalHours(emp.task_hours).toFixed(1) }}h</span>
                    </div>
                    <div *ngFor="let entry of getTaskBreakdown(emp.task_hours)"
                         class="text-xs text-gray-600 dark:text-gray-400">
                        <span class="font-semibold uppercase">{{ entry.task }}:</span> {{ entry.label }}
                    </div>
                </div>
                <button *ngIf="emp.employee_id === data.highlightEmployeeId"
                        mat-icon-button
                        matTooltip="Edit Timesheet"
                        (click)="editTimesheet()"
                        class="self-center h-8 w-8 ml-0.5 text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">
                    <mat-icon [svgIcon]="'tabler_outline:pencil'" class="icon-size-4"></mat-icon>
                </button>
            </div>
        </div>

        <!-- Equipment -->
        <div *ngIf="activeEquipment.length > 0">
            <h3 class="text-sm font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-1">
                <mat-icon [svgIcon]="'tabler_outline:backhoe'" class="icon-size-4 text-orange-500"></mat-icon>
                Equipment
            </h3>
            <div *ngFor="let eq of activeEquipment"
                 class="ml-2 mb-2 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
                <div class="flex items-center justify-between">
                    <span class="font-semibold text-sm text-gray-900 dark:text-white">{{ eq.name }}</span>
                    <span class="text-xs font-bold text-orange-600 dark:text-orange-400">{{ totalHours(eq.task_hours).toFixed(1) }}h</span>
                </div>
                <div *ngFor="let entry of getEquipmentTaskBreakdown(eq.task_hours)"
                     class="text-xs text-gray-600 dark:text-gray-400">
                    <span class="font-semibold uppercase">{{ entry.task }}:</span> {{ entry.label }}
                </div>
            </div>
        </div>

        <!-- Rentals -->
        <div *ngIf="activeRentals.length > 0">
            <h3 class="text-sm font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-1">
                <mat-icon [svgIcon]="'tabler_outline:crane'" class="icon-size-4 text-purple-500"></mat-icon>
                Rentals
            </h3>
            <div *ngFor="let r of activeRentals"
                 class="ml-2 mb-2 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
                <div class="flex items-center justify-between">
                    <span class="font-semibold text-sm text-gray-900 dark:text-white">{{ r.name }}</span>
                    <span class="text-xs font-bold text-purple-600 dark:text-purple-400">{{ totalHours(r.task_hours).toFixed(1) }}h</span>
                </div>
                <div *ngFor="let entry of getEquipmentTaskBreakdown(r.task_hours)"
                     class="text-xs text-gray-600 dark:text-gray-400">
                    <span class="font-semibold uppercase">{{ entry.task }}:</span> {{ entry.label }}
                </div>
            </div>
        </div>

        <!-- Materials -->
        <div *ngIf="activeMaterials.length > 0">
            <h3 class="text-sm font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-1">
                <mat-icon [svgIcon]="'tabler_outline:package'" class="icon-size-4 text-green-500"></mat-icon>
                Materials
            </h3>
            <div *ngFor="let mat of activeMaterials"
                 class="ml-2 mb-2 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
                <div class="font-semibold text-sm text-gray-900 dark:text-white">{{ materialDisplayName(mat) }}</div>
                <div *ngFor="let entry of getMaterialTaskBreakdown(mat)"
                     class="text-xs text-gray-600 dark:text-gray-400">
                    <span class="font-semibold uppercase">{{ entry.task }}:</span> {{ entry.label }}
                </div>
                <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Total: {{ totalQuantity(mat) }} {{ mat.unit }}
                </div>
            </div>
        </div>

        <!-- Task Production -->
        <div *ngIf="productionTasks.length > 0">
            <h3 class="text-sm font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-1">
                <mat-icon [svgIcon]="'tabler_outline:list-check'" class="icon-size-4 text-teal-500"></mat-icon>
                Task Production
            </h3>
            <div *ngFor="let task of productionTasks"
                 class="ml-2 mb-1 text-sm text-gray-700 dark:text-gray-300">
                <span class="font-semibold">{{ task.name }}:</span> {{ task.quantity }} {{ task.unit }}
            </div>
        </div>

        <!-- Notes -->
        <div *ngIf="ts.notes?.trim()">
            <h3 class="text-sm font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-1">
                <mat-icon [svgIcon]="'tabler_outline:notes'" class="icon-size-4 text-gray-500"></mat-icon>
                Notes
            </h3>
            <p class="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line ml-2">{{ ts.notes }}</p>
        </div>

        <!-- Incident Report -->
        <div *ngIf="ts.incidentReport?.trim()">
            <h3 class="text-sm font-bold text-red-600 dark:text-red-400 mb-2 flex items-center gap-1">
                <mat-icon [svgIcon]="'tabler_outline:alert-triangle'" class="icon-size-4"></mat-icon>
                Incident Report
            </h3>
            <p class="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line ml-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">{{ ts.incidentReport }}</p>
        </div>
    </div>

    <!-- Footer -->
    <div class="flex items-center justify-between px-5 py-3 border-t border-gray-200 dark:border-gray-700">
        <div>
            <button *ngIf="data.mode === 'notification-review'" mat-button class="!text-gray-500" (click)="printTimesheet()">
                <mat-icon svgIcon="tabler_outline:printer" class="icon-size-4 mr-1"></mat-icon>
                Print
            </button>
        </div>
        <div class="flex gap-2">
            <button *ngIf="data.mode === 'notification-review'" mat-stroked-button (click)="editTimesheet()">
                Edit
            </button>
            <button *ngIf="data.mode === 'notification-review'" mat-flat-button color="primary" (click)="approve()">
                Approve
            </button>
            <button *ngIf="data.mode !== 'notification-review'" mat-flat-button (click)="close()">Close</button>
        </div>
    </div>
</div>
